import { calculateCropRect } from "@/react/utils";
import { getBoxSize } from "@/react/utils";
export const templateFontList = [
    "Anton",
    "Bebas Neue",
    "Comfortaa",
    "Dancing Script",
    "Graduate",
    "Lobster",
    "Lora",
    "Montserrat",
    "Oswald",
    "Pacifico",
    "Playfair Display",
    "Racing Sans One",
    "Roboto",
    "Sacramento",
    "Tourney",
];
/**
 * Whether the image in a template image resource supports greenscreen
 * background selection. Only greenscreen images (PNGs with transparency) do.
 *
 * We only disable the background controls when we positively know the image is
 * not a greenscreen image. When unknown (e.g. an own-uploaded picture that is
 * not part of the loaded picture set), we keep the previous behaviour and
 * treat it as supported.
 */
export function imageSupportsBackground(image, pictureSet) {
    var _a;
    if (!(image === null || image === void 0 ? void 0 : image.uuid)) {
        return true;
    }
    if (typeof image.greenscreen === "boolean") {
        return image.greenscreen;
    }
    const picture = (_a = pictureSet === null || pictureSet === void 0 ? void 0 : pictureSet.pictures) === null || _a === void 0 ? void 0 : _a.find((p) => p.uuid === image.uuid);
    if (picture) {
        return picture.greenscreen;
    }
    return true;
}
export function imageHasTag(image, inputTag) {
    const allTags = inputTag
        .split(/\|/)
        .map((tag) => tag.trim())
        .filter(Boolean);
    const positiveTags = allTags.filter((tag) => !isNegativeOnlyTagGroup(tag));
    const tagsToCheck = positiveTags.length > 0 ? positiveTags : allTags;
    for (const tag of tagsToCheck) {
        if (imageHasSingleTag(image, tag)) {
            return true;
        }
    }
    return false;
}
function isNegativeOnlyTagGroup(tagGroup) {
    const tags = tagGroup
        .split(/[:/]/)
        .map((tag) => tag.trim())
        .filter(Boolean);
    return tags.length > 0 && tags.every((tag) => tag.startsWith("!"));
}
export function getRenderOrientation(renderImage, rotation) {
    var _a;
    let renderWidth = renderImage.size.width;
    let renderHeight = renderImage.size.height;
    const renderRotation = (_a = renderImage.rotation) !== null && _a !== void 0 ? _a : 0;
    // Normalize to 0–359
    let finalRotation = (renderRotation + rotation) % 360;
    if (finalRotation < 0) {
        finalRotation += 360;
    }
    // 90° / 270° swaps width and height
    if (finalRotation === 90 || finalRotation === 270) {
        [renderWidth, renderHeight] = [renderHeight, renderWidth];
    }
    console.log("Render orientation", renderImage, renderImage.size.height, renderImage.size.width, renderWidth, renderHeight, finalRotation, renderRotation, rotation, renderImage.rotation);
    return renderHeight >= renderWidth ? "portrait" : "landscape";
}
const imageHasSingleTag = (image, tag) => {
    var _a;
    const keywords = (_a = image.keywords) !== null && _a !== void 0 ? _a : [];
    if (tag.includes(":")) {
        const tags = tag.split(/:/g);
        // Must include all tags:
        for (const tag of tags) {
            if (!imageHasSingleTag(image, tag)) {
                return false;
            }
        }
        return true;
    }
    else if (tag.includes("/")) {
        const tags = tag.split(/\//g);
        // Must include one tag:
        for (const tag of tags) {
            if (imageHasSingleTag(image, tag)) {
                return true;
            }
        }
        return false;
    }
    if (tag.startsWith("!")) {
        const negativeTag = tag.slice(1);
        if (!negativeTag) {
            return false;
        }
        return keywords.indexOf(negativeTag) < 0;
    }
    return keywords.indexOf(tag) >= 0;
};
export function getCropRectForImageWithRotation(imageSize, renderPart, imageRotation, portraitOrientationGravity) {
    const pictureIsLandscape = imageSize.width > imageSize.height;
    console.log("[CROPRECT] Init ", imageSize, imageRotation, renderPart.rotation, portraitOrientationGravity);
    let rotatedImageSize = {
        width: imageSize.width,
        height: imageSize.height,
    };
    if (imageRotation === 90 || imageRotation === 270) {
        console.log("[CROPRECT] Flipping image size because of image rotation " +
            imageRotation);
        rotatedImageSize = {
            width: rotatedImageSize.height,
            height: rotatedImageSize.width,
        };
    }
    // 2) Establish render-part size used for fitting math (matches TemplateDraw swap-for-math behavior)
    let renderPartSizeForFit = {
        width: renderPart.size.width,
        height: renderPart.size.height,
    };
    if (renderPart.rotation === 90 || renderPart.rotation === 270) {
        console.log("[CROPRECT] Flipping dest area because of render part rotation rotation " +
            renderPart.rotation);
        renderPartSizeForFit = {
            width: renderPartSizeForFit.height,
            height: renderPartSizeForFit.width,
        };
    }
    // 3) Compute placement of the rotated bitmap into the (possibly swapped) render-part box.
    // This corresponds to XFunctions::getImageSizeInfo + computeCropRect in PHP, but expressed via your JS helpers.
    const pos = getBoxSize(renderPart.image.adaption, rotatedImageSize, renderPartSizeForFit);
    // 4) Convert placement -> crop rectangle in rotated bitmap coordinates
    let cropRect = calculateCropRect(pos, rotatedImageSize, renderPartSizeForFit);
    console.log("[CROPRECT] Crop rect is ", cropRect);
    // 5) Portrait "top" gravity adjustment (rotation-aware, in rotated-bitmap coords)
    if (portraitOrientationGravity === "top" && !pictureIsLandscape) {
        switch (imageRotation) {
            case 0:
                cropRect = Object.assign(Object.assign({}, cropRect), { y: 0 });
                break;
            case 90:
                cropRect = Object.assign(Object.assign({}, cropRect), { x: rotatedImageSize.width - cropRect.width });
                break;
            case 180:
                cropRect = Object.assign(Object.assign({}, cropRect), { y: rotatedImageSize.height - cropRect.height });
                break;
            case 270:
                cropRect = Object.assign(Object.assign({}, cropRect), { x: 0 });
                break;
            default:
                cropRect = Object.assign(Object.assign({}, cropRect), { y: 0 });
                break;
        }
    }
    // 6) Clamp (bounds + non-negative)
    cropRect = clampRect(cropRect, rotatedImageSize);
    return cropRect;
}
function clampRect(rect, imageSize) {
    let { x, y, width, height } = rect;
    if (x < 0)
        x = 0;
    if (y < 0)
        y = 0;
    if (width < 1)
        width = 1;
    if (height < 1)
        height = 1;
    if (x + width > imageSize.width)
        width = imageSize.width - x;
    if (y + height > imageSize.height)
        height = imageSize.height - y;
    // Re-guard in case imageSize was tiny
    if (width < 1)
        width = 1;
    if (height < 1)
        height = 1;
    return { x, y, width, height };
}
